import type {
  SessionTakeoverResponse,
  SessionReleaseResponse,
  SessionMessageSendResponse,
  SessionConversationResponse,
} from '@lasoxp/contracts';
import { auth } from '../config/firebase';
import { API_CONFIG } from '../config/api';
import type { ActiveSession } from '../types';

/**
 * Recursively normalize Firestore Timestamp objects ({ _seconds, _nanoseconds })
 * to ISO strings so components can safely use `new Date(value)` or display directly.
 * Also handles the `{ seconds, nanoseconds }` variant from Firestore REST responses.
 */
function normalizeTimestamps(obj: unknown): unknown {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(normalizeTimestamps);

  const record = obj as Record<string, unknown>;

  // Detect Firestore Timestamp shapes
  if (('_seconds' in record && '_nanoseconds' in record) ||
      ('seconds' in record && 'nanoseconds' in record)) {
    const seconds = (record._seconds ?? record.seconds) as number;
    return new Date(seconds * 1000).toISOString();
  }

  const result: Record<string, unknown> = {};
  for (const key of Object.keys(record)) {
    result[key] = normalizeTimestamps(record[key]);
  }
  return result;
}

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_CONFIG.baseUrl;
  }

  private async getAuthToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (!user) return null;
    return user.getIdToken();
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = await this.getAuthToken();
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      // Build message: use .error (backend convention), fall back to .message, append validation details
      let msg = error.error || error.message || `HTTP ${response.status}: ${response.statusText}`;
      if (Array.isArray(error.details) && error.details.length > 0) {
        const fieldErrors = error.details.map((d: { field?: string; message?: string }) =>
          d.field ? `${d.field}: ${d.message}` : d.message,
        ).join('; ');
        msg += ` (${fieldErrors})`;
      }
      throw new Error(msg);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as unknown as T;
    }

    const data = await response.json();
    return normalizeTimestamps(data) as T;
  }

  // ---------------------------------------------------------------------------
  // Generic convenience methods (use these for ad-hoc calls)
  // ---------------------------------------------------------------------------

  async get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, options);
  }

  async post<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  }

  async del<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // ============================================================================
  // Sessions API
  // ============================================================================

  async getActiveSessions(propertyId: string): Promise<ActiveSession[]> {
    return this.request<ActiveSession[]>(
      API_CONFIG.endpoints.sessions.active(propertyId)
    );
  }

  async getSessionDetail(sessionId: string, propertyId: string): Promise<ActiveSession> {
    return this.request<ActiveSession>(
      API_CONFIG.endpoints.sessions.detail(sessionId, propertyId)
    );
  }

  async takeoverSession(sessionId: string, agentId: string, propertyId: string, agentName?: string): Promise<SessionTakeoverResponse> {
    return this.request<SessionTakeoverResponse>(
      API_CONFIG.endpoints.sessions.takeover(sessionId),
      {
        method: 'POST',
        body: JSON.stringify({ agentId, agentName, propertyId }),
      }
    );
  }

  async releaseSession(sessionId: string, propertyId: string): Promise<SessionReleaseResponse> {
    return this.request<SessionReleaseResponse>(
      API_CONFIG.endpoints.sessions.release(sessionId),
      {
        method: 'POST',
        body: JSON.stringify({ propertyId }),
      }
    );
  }

  async sendSessionMessage(
    sessionId: string,
    message: string,
    agentId: string,
    propertyId: string,
    agentName?: string,
  ): Promise<SessionMessageSendResponse> {
    return this.request<SessionMessageSendResponse>(
      API_CONFIG.endpoints.sessions.message(sessionId),
      {
        method: 'POST',
        body: JSON.stringify({ message, agentId, agentName, propertyId }),
      }
    );
  }

  async getSessionConversation(sessionId: string, propertyId?: string): Promise<SessionConversationResponse> {
    return this.request<SessionConversationResponse>(
      API_CONFIG.endpoints.sessions.conversation(sessionId, propertyId),
    );
  }

}

export const apiService = new ApiService();
